import { supabase } from './supabase';

export interface MRRAnalytics {
  current_mrr: number;
  subscription_count: number;
  last_week_mrr: number;
  last_month_mrr: number;
}

export interface UserGrowth {
  total_users: number;
  today_signups: number;
  this_week_signups: number;
  last_week_signups: number;
  this_month_signups: number;
  last_month_signups: number;
}

export interface EngagementMetrics {
  dau_today: number;
  dau_yesterday: number;
  mau: number;
  feedback_requests_7d: number;
  feedback_responses_7d: number;
}

export interface ChurnAnalytics {
  active_subscriptions: number;
  churned_30d: number;
  churned_7d: number;
  churn_rate_30d: number;
}

export interface RevenueBreakdown {
  plan_type: string;
  subscription_count: number;
  total_revenue: number;
  percentage_of_total: number;
}

export interface FeedbackAnalytics {
  total_requests: number;
  total_responses: number;
  total_audio_responses: number;
  overall_response_rate: number;
}

export interface AIUsage {
  total_summaries: number;
  avg_responses_per_summary: number;
  summaries_last_7d: number;
  summaries_last_30d: number;
}

export interface RecentActivity {
  event_type: 'signup' | 'subscription' | 'feedback_request' | 'feedback_response';
  related_id: string;
  created_at: string;
  event_data: Record<string, any>;
}

export interface FounderAlert {
  id: string;
  alert_type: string;
  severity: 'info' | 'warning' | 'critical';
  title: string;
  message: string;
  metric_value: number | null;
  threshold_value: number | null;
  sent_at: string;
  email_sent: boolean;
  created_at: string;
}

export interface ComparisonMetric {
  current: number;
  previous: number;
  change: number;
  changePercent: number;
  trend: 'up' | 'down' | 'stable';
}

export const founderAnalyticsService = {
  async getMRRAnalytics(): Promise<MRRAnalytics | null> {
    const { data, error } = await supabase
      .from('founder_mrr_analytics')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching MRR analytics:', error);
      return null;
    }

    return data;
  },

  async getUserGrowth(): Promise<UserGrowth | null> {
    const { data, error } = await supabase
      .from('founder_user_growth')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching user growth:', error);
      return null;
    }

    return data;
  },

  async getEngagementMetrics(): Promise<EngagementMetrics | null> {
    const { data, error } = await supabase
      .from('founder_engagement_metrics')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching engagement metrics:', error);
      return null;
    }

    return data;
  },

  async getChurnAnalytics(): Promise<ChurnAnalytics | null> {
    const { data, error } = await supabase
      .from('founder_churn_analytics')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching churn analytics:', error);
      return null;
    }

    return data;
  },

  async getRevenueBreakdown(): Promise<RevenueBreakdown[]> {
    const { data, error } = await supabase
      .from('founder_revenue_breakdown')
      .select('*');

    if (error) {
      console.error('Error fetching revenue breakdown:', error);
      return [];
    }

    return data || [];
  },

  async getFeedbackAnalytics(): Promise<FeedbackAnalytics | null> {
    const { data, error } = await supabase
      .from('founder_feedback_analytics')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching feedback analytics:', error);
      return null;
    }

    return data;
  },

  async getAIUsage(): Promise<AIUsage | null> {
    const { data, error } = await supabase
      .from('founder_ai_usage')
      .select('*')
      .maybeSingle();

    if (error) {
      console.error('Error fetching AI usage:', error);
      return null;
    }

    return data;
  },

  async getRecentActivity(limit: number = 50): Promise<RecentActivity[]> {
    const { data, error } = await supabase
      .from('founder_recent_activity')
      .select('*')
      .limit(limit);

    if (error) {
      console.error('Error fetching recent activity:', error);
      return [];
    }

    return data || [];
  },

  async getAlerts(limit: number = 50): Promise<FounderAlert[]> {
    const { data, error } = await supabase
      .from('founder_alerts')
      .select('*')
      .order('sent_at', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching founder alerts:', error);
      return [];
    }

    return data || [];
  },

  calculateComparison(current: number, previous: number): ComparisonMetric {
    const change = current - previous;
    const changePercent = previous > 0 ? ((change / previous) * 100) : 0;

    let trend: 'up' | 'down' | 'stable' = 'stable';
    if (Math.abs(changePercent) > 5) {
      trend = changePercent > 0 ? 'up' : 'down';
    }

    return {
      current,
      previous,
      change,
      changePercent: Math.round(changePercent * 10) / 10,
      trend
    };
  },

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  },

  formatNumber(num: number): string {
    return new Intl.NumberFormat('en-US').format(num);
  },

  formatPercent(num: number): string {
    return `${num >= 0 ? '+' : ''}${num.toFixed(1)}%`;
  },

  async getAllDashboardData() {
    const [
      mrr,
      userGrowth,
      engagement,
      churn,
      revenue,
      feedback,
      aiUsage,
      activity,
      alerts
    ] = await Promise.all([
      this.getMRRAnalytics(),
      this.getUserGrowth(),
      this.getEngagementMetrics(),
      this.getChurnAnalytics(),
      this.getRevenueBreakdown(),
      this.getFeedbackAnalytics(),
      this.getAIUsage(),
      this.getRecentActivity(20),
      this.getAlerts(10)
    ]);

    return {
      mrr,
      userGrowth,
      engagement,
      churn,
      revenue,
      feedback,
      aiUsage,
      activity,
      alerts
    };
  }
};
